import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Parser } from "html-to-react";
import { trackPromise } from "react-promise-tracker";

// Redux slices and actions
import { updateStatus } from "src/slices/userSlice";

// Custom hooks
import { useListTableState } from "src/hooks/useListTableState";
import { useDialogBox } from "src/hooks/useDialogBox";
import { useUsersData } from "src/hooks/useUsersData";

// Layout/UI
import {
	Box,
	ButtonGroup,
	Checkbox,
	Icon,
	IconButton,
	Pagination,
	Switch,
	Table,
	MetreaTooltip,
} from "@metrea-ui/v2";
import RecordNav from "src/components/UI/RecordNav/RecordNav";
import { EnhancedTableHead, EnhancedTableToolbar } from "src/components/UI/common/EnhancedTableParts";
import CardList from "src/components/UI/CardList/CardList";
import styles from "./UsersList.module.css";
import { PeopleSwapFilled, ChevronLeftRegular, ChevronRightRegular } from "@fluentui/react-icons";

// Utilities
import {
	Session,
	loadUserProfile,
	applyRedirectTarget,
	isAdmin,
	isDevTeam,
	isDeveloper,
} from "src/utils/sessionUtils";
import { debugLog, errorLog, isDevSiteNow, showTesting } from "src/utils/debugUtils";
import { getShortDateString, getShortDateTimeString, highlight } from "src/utils/formatUtils";
import {
	getNextUserStatus,
	getUserStatusActionLabel,
	getUserStatusChangeVerb,
	getUserStatusLabel,
} from "./userStatus";

const normalizeEmail = (email) =>
	String(email || "")
		.trim()
		.toLowerCase();
const isPersistedUserId = (id) => {
	const userId = Number(id);
	return Number.isSafeInteger(userId) && userId > 0;
};
const searchFields = ["full_name", "email", "organization", "user_roles_desc"];

const UsersList = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;
	const { onSelectUser } = props;

	const dispatch = useDispatch();
	const { showDialog, withError } = useDialogBox();
	const { fetchUsers } = useUsersData(); // central thunk wrapper
	const htmlParser = new Parser();

	// --- Redux Selectors ---
	const usersList = useSelector((state) => state.users?.list ?? []);

	// --- Local State ---
	const [showInactive, setShowInactive] = useState(false);
	const [itemSelected, setItemSelected] = useState([]);
	const currentUserEmail = normalizeEmail(Session().currUser.email);

	const [searchKeyword, setSearchKeyword] = useState("");

	useEffect(() => {
		debugLog(2, "[UsersList][FLOW] mounted", {});
	}, []);

	// Helper to return filtered users based on Active/Inactive toggle.
	const visibleUsers = useMemo(() => {
		const activeFiltered = showInactive ? usersList : usersList.filter((x) => Number(x.status_id) === 1);
		if (!searchKeyword) return activeFiltered;

		const kw = searchKeyword.toLowerCase();
		const result = activeFiltered.filter((u) =>
			searchFields.some((field) => {
				const val = u[field];
				return typeof val === "string" && val.toLowerCase().includes(kw);
			}),
		);
		debugLog(4, "[UsersList][PROCESS] visibleUsers filter fields", { searchFields: searchFields });

		return result;
	}, [usersList, showInactive, searchKeyword]);

	// --- Controlled selection handler (notifies parent on user change only)
	const handleSelectionChange = (newSelected) => {
		debugLog(3, "[UsersList][ACTION] handleSelectionChange", { count: newSelected?.length ?? 0 });
		setItemSelected(newSelected);
	};

	// Table columns config
	const headCells = [
		{
			id: "email",
			numeric: false,
			disablePadding: false,
			label: "Email",
		},
		{
			id: "full_name",
			numeric: false,
			disablePadding: false,
			label: "Fullname",
		},
		{
			id: "roles",
			numeric: false,
			disablePadding: false,
			label: "Roles",
			disableSort: true,
		},
		{
			id: "organization",
			numeric: false,
			disablePadding: false,
			label: "Organization",
		},
		{
			id: "job_title",
			numeric: false,
			disablePadding: false,
			label: "Job Title",
		},
		{
			id: "lastAccessAt",
			numeric: false,
			disablePadding: false,
			label: "Last Login",
		},
		{
			id: "updatedAt",
			numeric: false,
			disablePadding: false,
			label: "Last Updated",
		},
		{
			id: "status_id",
			numeric: false,
			disablePadding: false,
			label: "Status",
		},
	];
	if (isDevTeam()) {
		headCells.push({
			id: "actingAs",
			numeric: false,
			disablePadding: false,
			label: " ",
		});
	}
	if (showTesting() && isDevTeam()) {
		headCells.unshift({
			id: "uuid",
			numeric: false,
			disablePadding: false,
			label: "UUID",
		});
	}

	// Keep the users sort menu aligned with the fields this screen actually
	// exposes instead of advertising generic RecordNav defaults.
	const sortByOptions = [
		{ disabled: false, id: 1, orderBy: "email", order: "asc", label: "Email (asc)" },
		{ disabled: false, id: 2, orderBy: "email", order: "desc", label: "Email (desc)" },
		{ disabled: false, id: 3, orderBy: "organization", order: "asc", label: "Organization (asc)" },
		{ disabled: false, id: 4, orderBy: "organization", order: "desc", label: "Organization (desc)" },
		{ disabled: false, id: 5, orderBy: "job_title", order: "asc", label: "Job Title (asc)" },
		{ disabled: false, id: 6, orderBy: "job_title", order: "desc", label: "Job Title (desc)" },
		{ disabled: false, id: 7, orderBy: "lastAccessAt", order: "asc", label: "Last Login (asc)" },
		{ disabled: false, id: 8, orderBy: "lastAccessAt", order: "desc", label: "Last Login (desc)" },
		{ disabled: false, id: 11, orderBy: "updatedAt", order: "asc", label: "Update Date (asc)" },
		{ disabled: false, id: 12, orderBy: "updatedAt", order: "desc", label: "Update Date (desc)" },
		{ disabled: false, id: 31, orderBy: "full_name", order: "asc", label: "Fullname (asc)" },
		{ disabled: false, id: 32, orderBy: "full_name", order: "desc", label: "Fullname (desc)" },
		{ disabled: false, id: 41, orderBy: "status_id", order: "asc", label: "Status (asc)" },
		{ disabled: false, id: 42, orderBy: "status_id", order: "desc", label: "Status (desc)" },
	];

	const compactDateCellStyle = { fontSize: "0.7rem" };

	// Custom hooks
	const {
		order,
		setOrder,
		orderBy,
		setOrderBy,
		setPage,
		pageM1,
		setPageM1,
		rowsPerPage,
		setRowsPerPage,
		dense,
		selected,
		visibleRows,
		emptyRows,
		handleRequestSort,
		handleChangePageM1,
		handleChangeDense,
		handleSelectAllClick,
		isSelected,
		checkHandler,
	} = useListTableState({
		items: visibleUsers, // Or from Redux selector
		initialOrderBy: "updatedAt",
		initialOrder: "desc",
		initialRowsPerPage: 10,
		initialPage: 1,
		selectionMode: true,
		selected: itemSelected,
		onSelectedChange: handleSelectionChange,
	});

	// Use useCallback to memoize and prevent unnecessary re-renders
	const rowClickHandler = useCallback(
		(user) => {
			// Non-positive IDs are system/service records, not editable User rows.
			if (!isPersistedUserId(user?.id)) return;
			debugLog(3, "[UsersList][ACTION] rowClickHandler", { userId: user?.id, userUuid: user?.uuid });

			// It's dispatching setCurrentUser(user) without validating whether this user contains the most up-to-date data.
			// If a stale or partial user object is passed in, the EditUser screen might render outdated data.
			// dispatch(setCurrentUser(user));

			// In-app Open should resolve by numeric id; UUID is reserved for explicit deep-link flows.
			onSelectUser(user.id);
		},
		[onSelectUser],
	);
	// UUID column is display/debug only; in-app open still resolves by numeric id.
	const handleRowOpenFromUuidColumn = (event, user) => {
		if (!isPersistedUserId(user?.id)) return;
		debugLog(3, "[UsersList][ACTION] handleRowOpenFromUuidColumn", {
			userId: user?.id,
			userUuid: user?.uuid,
		});
		onSelectUser(user?.id);
	};

	const changeStatusHandler = (o, newStatus, showMsgBox = true) => {
		debugLog(3, "[UsersList][ACTION] changeStatusHandler", {
			id: o.id,
			name: o.name,
			newStatus: newStatus,
			user: o,
		});

		const strAction = getUserStatusChangeVerb(newStatus);
		if (!strAction) return;
		debugLog(2, "[UsersList][FLOW] updateStatus started", {
			id: o.id,
			newStatus: newStatus,
			action: strAction,
		});

		trackPromise(
			dispatch(
				updateStatus({
					id: o.id,
					status_id: newStatus,
					updatedBy: Session().currUser.id,
				}),
			)
				.unwrap()
				.then(() => {
					debugLog(3, "[UsersList][STATE] user status updated", {
						id: o.id,
						newStatus: newStatus,
						action: strAction,
					});

					if (showMsgBox) {
						showDialog(
							{
								title: "Successful",
								message: [`The User "${o?.full_name}" was ${strAction} successfully!`],
							},
							() => {
								// refresh list so CardList rows come from fresh Redux state
								fetchUsers();
							},
						);
					} else {
						fetchUsers();
					}

				})
				.catch((error) => {
					errorLog(2, "[UsersList][FLOW] updateStatus failed", {
						id: o?.id,
						newStatus: newStatus,
						error: error,
					});
					console.error("updateStatus failed", error);
					// Status changes mutate server state, so keep the shared error
					// contract but avoid a retry button that could repeat the write.
					withError(error, null, {
						fallbackMessage: "User status update failed.",
					});
				}),
		);
	};

	// Handler for "Acting As" another user (developer tool)
	async function actingAsHandler(event, email) {
		debugLog(3, "[UsersList][ACTION] actingAsHandler", { email: email });
		debugLog(2, "[UsersList][FLOW] loadUserProfile started", { email: email });
		const result = await trackPromise(loadUserProfile(email, "currUser", "home", "actingAs"));
		if (!result?.ok) {
			errorLog(2, "[UsersList][FLOW] loadUserProfile failed", { email: email });
			// Acting-as changes session state, so surface a consistent message
			// without offering retry from the dialog.
			withError(result?.message || "Failed to switch acting user. Please try again.");
			return;
		}

		if (applyRedirectTarget(result.redirectTarget)) {
			return;
		}
	}

	// Handlers for RecordNav (sorting, show inactive, etc.)
	/**
	 * Handle keyword change emitted from RecordNav search box
	 * @param {string} keyword - The current search term from RecordNav
	 */
	const handleSearchChange = (keyword) => {
		setSearchKeyword(keyword);
		debugLog(3, "[UsersList][ACTION] handleSearchChange", { keyword: keyword });
	};

	const toggleInactiveHandler = (option) => {
		debugLog(3, "[UsersList][ACTION] toggleInactiveHandler", { option: option });

		setShowInactive(option);
	};

	const sortByHandler = (option) => {
		debugLog(3, "[UsersList][ACTION] sortByHandler", { option: option });

		setOrderBy(option.orderBy);
		setOrder(option.order);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[UsersList][ACTION] changeRowsPerPageHandler", { option: option });

		setRowsPerPage(option.id);
		props.setRowsPerPage(option.id);
		setPage(0);
		setPageM1(1);
	};

	// Inside your component (replace the current <Paper>...<Table.Root>...</Paper> block):
	const columns = [
		...(showTesting() && isDevTeam()
			? [
					{
						key: "uuid",
						title: "UUID",
						w: 240,
						render: (row) => row.uuid,
					},
				]
			: []),
		{
			key: "email",
			title: "Email",
			minW: 180,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.email, false)),
		},
		{
			key: "full_name",
			title: "Fullname",
			minW: 100,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.full_name, false)),
		},
		{
			key: "user_roles_desc",
			title: "Roles",
			minW: 120,
			stackGrow: 2,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.user_roles_desc, false)),
		},
		{
			key: "organization",
			title: "Organization",
			minW: 80,
			tooltip: "auto",
			render: (row) => htmlParser.parse(highlight(searchKeyword, row.organization, false)),
		},
		{
			key: "job_title",
			title: "Job Title",
			minW: 120,
			tooltip: "auto",
		},
		{
			key: "lastAccessAt",
			title: "Last Login",
			minW: 120,
			tooltip: "auto",
			render: (row) => getShortDateTimeString(row.lastAccessAt),
		},
		{
			key: "createdAt",
			title: "Date Created",
			minW: 120,
			tooltip: "auto",
			render: (row) => getShortDateString(row.createdAt),
		},
		{
			key: "updatedAt",
			title: "Last Updated",
			minW: 120,
			tooltip: "auto",
			render: (row) => getShortDateTimeString(row.updatedAt),
		},
		{
			key: "status_id",
			title: "Status",
			minW: 60,
			tooltip: undefined, // or omit the field to disable the ellipsis tooltip path
			render: (row) => getUserStatusLabel(row?.status_id),
		},
	];

	// --- Logging: Render and State Snapshots ---
	debugLog(9, "[UsersList][FLOW] render", {
		renderCount: renderCount.current,
		rowsPerPage: rowsPerPage,
		pageM1: pageM1,
		selectedCount: selected?.length ?? 0,
		visibleUserCount: visibleUsers?.length ?? 0,
		showInactive: showInactive,
	});

	return (
		<>
			<Box w="100%">
				{/* Replace Table/Paper block with CardList */}
				<CardList
					rows={visibleRows}
					keyField="id"
					columns={columns}
					toolbarSlot={
						<RecordNav
							data={visibleUsers}
							showSearch={isDeveloper()}
							searchFields={searchFields}
							columns={columns}
							onSearchChange={handleSearchChange}
							showToggleInactive
							showSortBy
							showRowsPerPage
							rowsPerPage={rowsPerPage}
							count={visibleRows.length}
							onToggleInactive={toggleInactiveHandler}
							onSortBy={(opt) => sortByHandler({ orderBy: opt.orderBy, order: opt.order })}
							onChangeRowsPerPage={(opt) => changeRowsPerPageHandler(opt)}
							callFrom="Listing"
							sortByOptions={sortByOptions}
							orderBy={orderBy}
							order={order}
						/>
					}
					showLabels
					density="compact"
					responsive={{
						mode: "auto",
						maxRows: 5,
						minColWidth: 100,
						gap: 12,
					}}
					showVerticalDividers
					paginationSlot={
						<Pagination.Root
							count={visibleUsers.length}
							pageSize={Number(rowsPerPage) || 10}
							page={pageM1}
							onPageChange={({ page }) => handleChangePageM1(undefined, page)}
						>
							<ButtonGroup variant="ghost" size="2xs">
								<Pagination.PrevTrigger asChild>
									<IconButton>
										<ChevronLeftRegular />
									</IconButton>
								</Pagination.PrevTrigger>
								<Pagination.Items
									render={(page) => (
										<IconButton variant={{ base: "ghost", _selected: "solid" }}>
											{page.value}
										</IconButton>
									)}
								/>
								<Pagination.NextTrigger asChild>
									<IconButton>
										<ChevronRightRegular />
									</IconButton>
								</Pagination.NextTrigger>
							</ButtonGroup>
						</Pagination.Root>
					}
					rowActions={(row) => {
						const actions = [];
						const userId = Number(row?.id);
						// Non-positive IDs cannot use user actions; keep the menu informational for the system account.
						if (!isPersistedUserId(userId)) {
							return [
								{
									id: "system-account-info",
									label: "Read-Only System account",
									disabled: true,
									triggerTooltip: "Read-Only System account",
								},
							];
						}

						actions.push({
							id: "open",
							label: "Open",
							onSelect: (row) => rowClickHandler(row),
						});

						const statusAction = getUserStatusActionLabel(row.status_id);
						if ((isAdmin() || isDeveloper()) && statusAction) {
							actions.push({
								id: "toggle-active",
								label: statusAction,
								onSelect: () => changeStatusHandler(row, getNextUserStatus(row.status_id)),
							});
						}

						if (isDevTeam() && getNextUserStatus(row.status_id) !== null) {
							actions.push({
								id: "divider",
								label: "-----------",
							});
							actions.push({
								id: "actingAs",
								label: `Acting As ${row.email}`,
								onSelect: (row) => actingAsHandler(null, row.email),
							});
						}

						return actions;
					}}
				/>
			</Box>

			{/* Keep the legacy table mirror behind testing mode while draining its
			    wrapper-backed controls onto direct v2 primitives. */}
			{showTesting() && isDeveloper() && (
				<>
					<Box marginTop="1200px"></Box>
					<Box w="100%">
						<RecordNav
							data={visibleUsers}
							showBack
							showToggleInactive
							showSortBy
							showRowsPerPage
							rowsPerPage={rowsPerPage}
							count={visibleRows.length}
							onToggleInactive={toggleInactiveHandler}
							onSortBy={sortByHandler}
							onChangeRowsPerPage={changeRowsPerPageHandler}
							sortByOptions={sortByOptions}
							orderBy={orderBy}
							order={order}
						/>
						<Box w="100%" mb={3} boxShadow="sm" bg="white" rounded="md">
							{showTesting() && isDevTeam() && (
								<EnhancedTableToolbar numSelected={selected.length} />
							)}

							<Box overflowX="auto">
								<Table.Root
									size={dense ? "sm" : "md"}
									variant="outline"
									aria-labelledby="tableTitle"
								>
									<EnhancedTableHead
										numSelected={selected.length}
										order={order}
										orderBy={orderBy}
										onSelectAllClick={handleSelectAllClick}
										onRequestSort={handleRequestSort}
										rowCount={visibleUsers.length}
										headCells={headCells}
										styles={styles}
									/>

									<Table.Body>
										{visibleRows.map((row) => {
											const isItemSelected = isSelected(row.id);

											return (
												<Table.Row
													key={row.id}
													data-selected={isItemSelected}
													style={{ cursor: "pointer" }}
													className={styles.tableBodyRow}
												>
													{/* Render UUID/checkbox for dev if enabled */}
													{showTesting() && isDevTeam() && (
														<Table.Cell>
															<Checkbox.Root
																checked={isItemSelected}
																onCheckedChange={(next) =>
																	checkHandler(
																		{
																			target: {
																				checked:
																					next === true ||
																					next?.checked === true ||
																					next?.detail?.checked ===
																						true,
																			},
																		},
																		row.id,
																	)
																}
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																}}
															>
																<Checkbox.HiddenInput />
																<Checkbox.Control />
															</Checkbox.Root>
														</Table.Cell>
													)}
													{showTesting() && isDevTeam() && (
														<Table.Cell
															onClick={(event) =>
																handleRowOpenFromUuidColumn(event, row)
															}
														>
															{row.uuid}
														</Table.Cell>
													)}
													{/* Main user fields: email, name, roles, etc. */}

													<Table.Cell
														style={{ width: "20%" }}
														onClick={() => rowClickHandler(row)}
													>
														{row.email}
													</Table.Cell>
													<Table.Cell onClick={() => rowClickHandler(row)}>
														{row.full_name}
													</Table.Cell>
													<Table.Cell onClick={() => rowClickHandler(row)}>
														{row.user_roles_desc}
													</Table.Cell>
													<Table.Cell onClick={() => rowClickHandler(row)}>
														{row.organization}
													</Table.Cell>
													<Table.Cell onClick={() => rowClickHandler(row)}>
														{row.job_title}
													</Table.Cell>
													<Table.Cell
														onClick={() => rowClickHandler(row)}
														style={compactDateCellStyle}
													>
														{row.lastAccessAt
															? getShortDateTimeString(row.lastAccessAt)
															: ""}
													</Table.Cell>
													<Table.Cell
														onClick={() => rowClickHandler(row)}
														style={compactDateCellStyle}
													>
														{getShortDateTimeString(row.updatedAt)}
													</Table.Cell>
													<Table.Cell onClick={() => rowClickHandler(row)}>
														{getUserStatusLabel(row.status_id)}
													</Table.Cell>
													{isDevTeam() &&
														isPersistedUserId(row?.id) &&
														(currentUserEmail !== normalizeEmail(row.email) ? (
															<Table.Cell>
																<MetreaTooltip
																	content={`Acting as ${row.email}`}
																	positioning={{ placement: "right-end" }}
																>
																	<Icon
																		as={PeopleSwapFilled}
																		boxSize="1.2rem"
																		onClick={(e) =>
																			actingAsHandler(e, row.email)
																		}
																	/>
																</MetreaTooltip>
															</Table.Cell>
														) : (
															<Table.Cell>&nbsp;</Table.Cell>
														))}
												</Table.Row>
											);
										})}
										{emptyRows > 0 && (
											<Table.Row style={{ height: (dense ? 33 : 53) * emptyRows }}>
												<Table.Cell
													colSpan={
														(showTesting() && isDevTeam() ? 1 : 0) +
														headCells.length
													}
													style={{ borderBottom: "none", padding: 0 }}
												/>
											</Table.Row>
										)}
									</Table.Body>
								</Table.Root>
							</Box>
						</Box>

						<Box className="flex-auto justify-items-center p-1">
							<Pagination.Root
								count={visibleUsers.length}
								pageSize={Number(rowsPerPage) || 10}
								page={pageM1}
								onPageChange={({ page }) => handleChangePageM1(undefined, page)}
							>
								<ButtonGroup variant="ghost" size="2xs">
									<Pagination.PrevTrigger asChild>
										<IconButton>
											<ChevronLeftRegular />
										</IconButton>
									</Pagination.PrevTrigger>
									<Pagination.Items
										render={(page) => (
											<IconButton variant={{ base: "ghost", _selected: "solid" }}>
												{page.value}
											</IconButton>
										)}
									/>
									<Pagination.NextTrigger asChild>
										<IconButton>
											<ChevronRightRegular />
										</IconButton>
									</Pagination.NextTrigger>
								</ButtonGroup>
							</Pagination.Root>
						</Box>

						{/* Toggle for dense layout (only in dev/test) */}
						{showTesting() && isDevTeam() && isDevSiteNow() && (
							<Switch.Root
								size="sm"
								className="m-[10px]"
								checked={dense}
								onCheckedChange={(next) =>
									handleChangeDense({
										target: {
											checked:
												next === true ||
												next?.checked === true ||
												next?.detail?.checked === true,
										},
									})
								}
							>
								<Switch.HiddenInput />
								<Switch.Control>
									<Switch.Thumb />
								</Switch.Control>
								<Switch.Label>Dense padding</Switch.Label>
							</Switch.Root>
						)}
					</Box>
				</>
			)}
		</>
	);
};

export default UsersList;
